import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { 
  Phone, Mail, Clock, MapPin, CheckCircle, AlertTriangle, 
  Send, ShieldAlert, Award 
} from "lucide-react";

export default function Contact() {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company_name: "",
    industry: "Painting",
    message: "",
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const indParam = searchParams.get("industry");
    if (indParam) {
      setFormData((prev) => ({ ...prev, industry: indParam }));
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
      const endpoint = `${backendUrl}/api/contact`;
      
      const response = await axios.post(endpoint, formData);
      if (response.status === 201 || response.status === 200) {
        setSuccess(true);
        // Clear form
        setFormData({
          name: "",
          email: "",
          phone: "",
          company_name: "",
          industry: "Painting",
          message: "",
        });
      } else {
        throw new Error("Invalid backend server status code response.");
      }
    } catch (err) {
      console.error("Failed to submit contact lead:", err);
      setError(
        err.response?.data?.detail || 
        "Failed to transmit lead to corporate server. Please verify your connection or try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white" data-testid="contact-page">
      
      {/* Page Header */}
      <section className="bg-slate-900 text-white py-16 md:py-24 text-center space-y-4">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 max-w-3xl space-y-3">
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-400">Secure Client Access</span>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-white leading-tight">
            Connect With Precision Home Leads LLC
          </h1>
          <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
            Our client onboarding managers are standing by to evaluate your service coverage territory. Use the secure B2B capture system below.
          </p>
        </div>
      </section>

      {/* Main Grid: Form + Sidebar details */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            
            {/* Contact Form Column (Left 7-cols) */}
            <div className="lg:col-span-7 bg-white border border-slate-200 rounded-lg p-8 md:p-10 shadow-sm" data-testid="contact-form-container">
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight mb-2">Secure Lead Capture Terminal</h2>
              <p className="text-xs text-slate-500 mb-8 leading-relaxed">
                All lead data transmission is encrypted via SSL. Information captured is used strictly for onboarding assessment and is subject to our Federal Consumer Privacy Policy.
              </p>

              {success && (
                <div className="mb-8 p-6 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-md flex items-start gap-3" data-testid="contact-success-alert">
                  <CheckCircle className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <h4 className="font-semibold text-sm">Lead Securely Transmitted!</h4>
                    <p className="text-xs text-emerald-700 leading-relaxed">
                      Thank you. Your contractor onboarding file has been successfully created in MongoDB. An onboarding strategist will review your zip codes and phone you within 24 business hours.
                    </p>
                  </div>
                </div>
              )}

              {error && (
                <div className="mb-8 p-6 bg-red-50 border border-red-200 text-red-800 rounded-md flex items-start gap-3" data-testid="contact-error-alert">
                  <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <h4 className="font-semibold text-sm">Transmission Error</h4>
                    <p className="text-xs text-red-700 leading-relaxed">
                      {error}
                    </p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">Full Name</label>
                    <input 
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="John Doe"
                      className="w-full px-3 py-2.5 border border-slate-200 rounded text-sm bg-slate-50/50 focus:outline-none focus:border-slate-950 focus:bg-white transition-all"
                      data-testid="contact-form-name-input"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">Business Email</label>
                    <input 
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="john@contractingcompany.com"
                      className="w-full px-3 py-2.5 border border-slate-200 rounded text-sm bg-slate-50/50 focus:outline-none focus:border-slate-950 focus:bg-white transition-all"
                      data-testid="contact-form-email-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">Business Phone</label>
                    <input 
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="(555) 555-5555"
                      className="w-full px-3 py-2.5 border border-slate-200 rounded text-sm bg-slate-50/50 focus:outline-none focus:border-slate-950 focus:bg-white transition-all"
                      data-testid="contact-form-phone-input"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">Company Name</label>
                    <input 
                      type="text"
                      required
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      placeholder="Precision Contractors LLC"
                      className="w-full px-3 py-2.5 border border-slate-200 rounded text-sm bg-slate-50/50 focus:outline-none focus:border-slate-950 focus:bg-white transition-all"
                      data-testid="contact-form-company-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">Target Specialty Trade</label>
                  <select
                    value={formData.industry}
                    onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded text-sm bg-white focus:outline-none focus:border-slate-950 transition-all"
                    data-testid="contact-form-industry-select"
                  >
                    {["Painting", "Concrete", "Tree Removal", "Junk Removal", "Landscaping", "Artificial Turf", "Remodeling", "Deck Builders", "Roofing", "Siding", "Gutters"].map((ind) => (
                      <option key={ind} value={ind}>{ind}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">Service Capacity / Territory Goals</label>
                  <textarea 
                    rows={4}
                    required
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Provide a list of targeted zip codes and details about your company's weekly job completion capacity..."
                    className="w-full px-3 py-2.5 border border-slate-200 rounded text-sm bg-slate-50/50 focus:outline-none focus:border-slate-950 focus:bg-white transition-all resize-none"
                    data-testid="contact-form-message-textarea"
                  />
                </div>

                <div className="pt-2">
                  <button 
                    type="submit"
                    disabled={loading}
                    className="w-full inline-flex items-center justify-center px-6 py-3.5 rounded bg-slate-900 hover:bg-slate-800 text-white font-semibold text-sm transition-colors disabled:opacity-50"
                    data-testid="contact-form-submit-button"
                  >
                    {loading ? "Transmitting lead encrypted..." : "Submit Secure Request"}
                    <Send className="ml-2 h-4 w-4" />
                  </button>
                </div>

                <p className="text-[10px] text-slate-400 text-center leading-relaxed">
                  By clicking above, you authorize Precision Home Leads LLC to save this contractor profile data and initiate standard commercial business communications as described in our Terms of Service.
                </p>

              </form>
            </div>

            {/* Sidebar Details & Map Placeholder (Right 5-cols) */}
            <div className="lg:col-span-5 space-y-8">
              
              {/* Corporate details */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-8 space-y-6">
                <h3 className="text-base font-bold text-slate-900 uppercase tracking-wider">Corporate Coordinates</h3>
                
                <ul className="space-y-5">
                  <li className="flex items-start gap-3">
                    <MapPin className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <p className="text-xs font-semibold text-slate-900 uppercase tracking-wider">Corporate Headquarters</p>
                      <p className="text-xs text-slate-500 leading-normal">
                        Precision Home Leads LLC<br />
                        616 Stearn Dr<br />
                        Genoa, IL 60135
                      </p>
                    </div>
                  </li>

                  <li className="flex items-start gap-3">
                    <Phone className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <p className="text-xs font-semibold text-slate-900 uppercase tracking-wider">Phone Support</p>
                      <p className="text-xs text-slate-500">
                        <a href="tel:+14707650187" className="hover:text-emerald-600 font-semibold" data-testid="sidebar-phone">+1 (470) 765-0187</a>
                      </p>
                    </div>
                  </li>

                  <li className="flex items-start gap-3">
                    <Mail className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <p className="text-xs font-semibold text-slate-900 uppercase tracking-wider">Onboarding Email</p>
                      <p className="text-xs text-slate-500">
                        <a href="mailto:contact@precisionleads.co" className="hover:text-emerald-600 font-semibold" data-testid="sidebar-email">contact@precisionleads.co</a>
                      </p>
                    </div>
                  </li>

                  <li className="flex items-start gap-3">
                    <Clock className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <p className="text-xs font-semibold text-slate-900 uppercase tracking-wider">Operating Business Hours</p>
                      <p className="text-xs text-slate-500">
                        Monday - Saturday: 8:00 AM - 7:00 PM CST<br />
                        Sunday: Closed
                      </p>
                    </div>
                  </li>
                </ul>

                <div className="border-t border-slate-200/80 pt-4 flex items-center gap-2 text-xs text-slate-400 font-medium">
                  <Award className="h-4 w-4 text-emerald-600" />
                  Merchant Underwriting Compliant Physical Site
                </div>
              </div>

              {/* Styled Maps Placeholder */}
              <div className="border border-slate-200 rounded-lg overflow-hidden shadow-sm" data-testid="google-maps-placeholder">
                <div className="bg-slate-900 text-white px-4 py-3 flex justify-between items-center text-xs">
                  <span className="font-semibold uppercase tracking-wider">Headquarters Map View</span>
                  <span className="text-[10px] text-emerald-400 font-bold">GENOA, IL</span>
                </div>
                {/* Styled placeholder simulating a high-tech corporate map */}
                <div className="relative h-64 bg-slate-100 flex flex-col items-center justify-center text-center p-6 space-y-4">
                  {/* Grid Lines Overlay */}
                  <div className="absolute inset-0 bg-[linear-gradient(to_right,#e2e8f0_1px,transparent_1px),linear-gradient(to_bottom,#e2e8f0_1px,transparent_1px)] bg-[size:1.5rem_1.5rem] opacity-40" />
                  
                  {/* Compass/GPS graphic */}
                  <div className="relative z-10 w-12 h-12 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center text-emerald-600 shadow-sm animate-pulse">
                    <MapPin className="h-6 w-6" />
                  </div>

                  <div className="relative z-10 space-y-1">
                    <p className="text-xs font-bold text-slate-800">616 Stearn Dr</p>
                    <p className="text-[11px] text-slate-500 max-w-xs leading-normal">
                      Genoa, Illinois 60135.<br />
                      Latitude/Longitude: 42.0959° N, 88.6926° W
                    </p>
                  </div>

                  <span className="relative z-10 text-[9px] uppercase font-bold tracking-widest text-slate-400 border border-slate-200 px-2 py-1 rounded bg-white shadow-inner">
                    PHYSICAL B2B PREMISES VERIFIED
                  </span>
                </div>
              </div>

            </div>

          </div>
        </div>
      </section>

    </div>
  );
}
